import { create } from "zustand";

import { ListIssueGroupsResponse_RuleGroup } from "@protos/issues/v1/issues";
import { Issue as IssueProto } from "@protos/issues/v1/issues";

interface IssueMap {
  [id: number]: IssueProto;
}

interface RuleGroupMap {
  [id: number]: ListIssueGroupsResponse_RuleGroup;
}

class IssueSet {
  _map: IssueMap;

  constructor(map = {}) {
    this._map = map;
  }

  // To work nicely with React, mutation methods will return a new object

  add(issue: IssueProto): IssueSet {
    this._map[issue.issueParentId] = issue;
    return new IssueSet(this._map);
  }

  delete(issue: IssueProto): IssueSet {
    delete this._map[issue.issueParentId];
    return new IssueSet(this._map);
  }

  clearGroup(ruleGroup: ListIssueGroupsResponse_RuleGroup): IssueSet {
    Object.values(this._map).forEach((issue) => {
      if (ruleGroup.ruleId === issue.ruleId) {
        delete this._map[issue.issueParentId];
      }
    });
    return new IssueSet(this._map);
  }

  issueCount(): number {
    return Object.keys(this._map).length;
  }

  issueParentIds() {
    return Object.values(this._map).map((i: IssueProto) => i.issueParentId);
  }
}

class RuleGroupSet {
  _map: RuleGroupMap;

  constructor(map = {}) {
    this._map = map;
  }

  // To work nicely with React, mutation methods will return a new object

  add(ruleGroup: ListIssueGroupsResponse_RuleGroup): RuleGroupSet {
    this._map[ruleGroup.ruleId] = ruleGroup;
    return new RuleGroupSet(this._map);
  }

  delete(ruleGroup: ListIssueGroupsResponse_RuleGroup): RuleGroupSet {
    delete this._map[ruleGroup.ruleId];
    return new RuleGroupSet(this._map);
  }

  issueCount(): number {
    return Object.values(this._map)
      .map((g) => g.issuesCount)
      .reduce((total, count) => total + count, 0);
  }

  ruleIds() {
    return Object.values(this._map).map(
      (g: ListIssueGroupsResponse_RuleGroup) => g.ruleId
    );
  }
}

interface SelectedGroupFindings {
  [key: string]: number[];
}
interface SelectAllFindingsWithinGroup {
  [key: string]: boolean;
}

export interface State {
  selectedFindings: number[];
  selectAllFindings: boolean;
  selectAllFindingsOnPage: boolean;
  selectedGroupFindings: SelectedGroupFindings;
  selectAllGroupFindings: SelectAllFindingsWithinGroup;
  selectedGroupFindingsCount: number;
  selectedFindingsV2: IssueSet;
  selectedRuleGroupsV2: RuleGroupSet;
}

interface Action {
  actions: {
    setFindingChecked: (findingId: number, checked: boolean) => void;
    setGroupFindingChecked: (
      ruleId: string,
      findingId: number,
      checked: boolean
    ) => void;
    selectAllGroupFindings: (
      ruleId: string,
      checked: boolean,
      findingCount: number
    ) => void;
    resetGroupFindings: (ruleId: string) => void;
    selectFinding: (findingId: number) => void;
    setSelectAllFindings: (state: boolean) => void;
    setSelectAllFindingsOnPage: (state: boolean) => void;
    resetFindings: () => void;
    resetSpecificFindings: (findingIds: number[]) => void;
    selectFindingV2: (issue: IssueProto) => void;
    unselectFindingV2: (issue: IssueProto) => void;
    selectRuleGroupV2: (ruleGroup: ListIssueGroupsResponse_RuleGroup) => void;
    unselectRuleGroupV2: (ruleGroup: ListIssueGroupsResponse_RuleGroup) => void;
  };
}

export const useTriageStore = create<State & Action>((set, get) => ({
  selectedFindings: [],
  selectAllFindings: false,
  selectAllFindingsOnPage: false,
  selectedGroupFindings: {},
  selectAllGroupFindings: {},
  selectedGroupFindingsCount: 0,
  selectedFindingsV2: new IssueSet(),
  selectedRuleGroupsV2: new RuleGroupSet(),

  actions: {
    setFindingChecked: (findingId: number, checked: boolean) => {
      const selectedFindings = get().selectedFindings;
      if (checked && !selectedFindings.includes(findingId)) {
        set({ selectedFindings: [...selectedFindings, findingId] });
      } else if (!checked && selectedFindings.includes(findingId)) {
        set({
          selectedFindings: selectedFindings.filter((id) => id !== findingId),
        });
      }
    },

    setGroupFindingChecked: (ruleId, findingId, checked) => {
      const selectedGroupFindings = get().selectedGroupFindings;
      if (selectedGroupFindings[ruleId]) {
        if (!checked && selectedGroupFindings[ruleId].includes(findingId)) {
          set({
            selectedGroupFindings: {
              ...selectedGroupFindings,
              [ruleId]: selectedGroupFindings[ruleId].filter(
                (id) => id !== findingId
              ),
            },
          });
        } else if (
          checked &&
          !selectedGroupFindings[ruleId].includes(findingId)
        ) {
          set({
            selectedGroupFindings: {
              ...selectedGroupFindings,
              [ruleId]: [...selectedGroupFindings[ruleId], findingId],
            },
          });
        }
      } else if (checked) {
        set({
          selectedGroupFindings: {
            ...selectedGroupFindings,
            [ruleId]: [findingId],
          },
        });
      }
    },

    selectAllGroupFindings: (ruleId, checked, findingCount) => {
      const selectAllGroupFindings = get().selectAllGroupFindings;
      const currentCount = get().selectedGroupFindingsCount;
      set({
        selectAllGroupFindings: {
          ...selectAllGroupFindings,
          [ruleId]: checked,
        },
      });

      if (checked) {
        set({
          selectedGroupFindingsCount: currentCount + findingCount,
        });
      } else {
        set({
          selectedGroupFindingsCount: currentCount - findingCount,
        });
      }
    },

    selectFinding: (findingId) => {
      const selectedFindings = get().selectedFindings;
      if (!selectedFindings.includes(findingId)) {
        set({ selectedFindings: [...selectedFindings, findingId] });
      }
    },

    setSelectAllFindings: (state) => {
      set({ selectAllFindings: state });
    },

    setSelectAllFindingsOnPage: (state) => {
      set({ selectAllFindingsOnPage: state });
    },

    resetGroupFindings: (ruleId) => {
      const selectedGroupFindings = get().selectedGroupFindings;
      set({
        selectedGroupFindings: {
          ...selectedGroupFindings,
          [ruleId]: [],
        },
        selectedGroupFindingsCount: 0,
      });
    },

    resetFindings: () => {
      set({
        selectedFindings: [],
        selectAllFindings: false,
        selectAllFindingsOnPage: false,
        selectedGroupFindings: {},
        selectAllGroupFindings: {},
        selectedGroupFindingsCount: 0,
        selectedFindingsV2: new IssueSet(),
        selectedRuleGroupsV2: new RuleGroupSet(),
      });
    },
    resetSpecificFindings: (findingIds) => {
      const selectedFindings = get().selectedFindings;
      set({
        selectedFindings: selectedFindings.filter((id) =>
          findingIds.includes(id)
        ),
      });
    },

    selectFindingV2: (issue: IssueProto) => {
      const { selectedFindingsV2 } = get();
      set({ selectedFindingsV2: selectedFindingsV2.add(issue) });
    },

    unselectFindingV2: (issue: IssueProto) => {
      const { selectedFindingsV2 } = get();
      set({ selectedFindingsV2: selectedFindingsV2.delete(issue) });
    },

    selectRuleGroupV2: (ruleGroup: ListIssueGroupsResponse_RuleGroup) => {
      const { selectedFindingsV2, selectedRuleGroupsV2 } = get();
      set({
        selectedRuleGroupsV2: selectedRuleGroupsV2.add(ruleGroup),
        // Remove findings for this group to not count them twice
        selectedFindingsV2: selectedFindingsV2.clearGroup(ruleGroup),
      });
    },

    unselectRuleGroupV2: (ruleGroup: ListIssueGroupsResponse_RuleGroup) => {
      const { selectedRuleGroupsV2 } = get();
      set({
        selectedRuleGroupsV2: selectedRuleGroupsV2.delete(ruleGroup),
      });
    },
  },
}));

export const useTriageState = () => useTriageStore((state) => state);
export const useTriageActions = () => useTriageStore((state) => state.actions);
export const useSelectedFindings = () =>
  useTriageStore((state) => state.selectedFindings);
export const useSelectAllFindings = () =>
  useTriageStore((state) => state.selectAllFindings);
export const useSelectAllFindingsOnPage = () =>
  useTriageStore((state) => state.selectAllFindingsOnPage);
export const useSelectedGroupFindings = () =>
  useTriageStore((state) => state.selectedGroupFindings);
export const useSelectAllGroupFindings = () =>
  useTriageStore((state) => state.selectAllGroupFindings);
export const useSelectedGroupFindingsCount = () =>
  useTriageStore((state) => state.selectedGroupFindingsCount);
export const useSelectedFindingsV2 = () =>
  useTriageStore((state) => state.selectedFindingsV2);
export const useSelectedRuleGroupsV2 = () =>
  useTriageStore((state) => state.selectedRuleGroupsV2);
